import React, { useState, useEffect } from "react";
import { FileCode, RefreshCw, Clock, AlertTriangle } from "lucide-react";
import axios from "axios";
import { API_BASE_URL, API_ENDPOINTS, REQUEST_CONFIG } from "../config";

/**
 * CodeQualityMetrics component displays high-level metrics from code quality reports.
 * Shows summary statistics for both backend (Golang) and frontend (ESLint) code quality.
 */
export default function CodeQualityMetrics() {
  const [codeQualityReport, setCodeQualityReport] = useState(null);
  const [eslintCodeQualityReport, setEslintCodeQualityReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [eslintReportLoading, setEslintReportLoading] = useState(false);
  
  // Fetch both code quality reports when component mounts
  useEffect(() => {
    fetchCodeQualityReport();
    fetchEslintCodeQualityReport();
  }, []);
  
  // Function to fetch the backend code quality report (Golang)
  const fetchCodeQualityReport = async () => {
    try {
      setReportLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.CODE.CODE_QUALITY_REPORT}`,
        { headers: REQUEST_CONFIG.getAuthHeaders() }
      );
      
      if (response.data && response.data.status === 'success') {
        setCodeQualityReport(response.data);
      } else {
        setCodeQualityReport({
          status: 'error',
          message: response.data.message || 'Failed to fetch report'
        });
      }
      setReportLoading(false);
    } catch (error) {
      setCodeQualityReport({
        status: 'error',
        message: error.message || 'An error occurred'
      });
      setReportLoading(false);
    }
  };
  
  // Function to fetch the frontend code quality report (ESLint)
  const fetchEslintCodeQualityReport = async () => {
    try {
      setEslintReportLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.CODE.ESLINT_CODE_QUALITY_REPORT}`,
        { headers: REQUEST_CONFIG.getAuthHeaders() }
      );
      
      if (response.data && response.data.status === 'success') {
        setEslintCodeQualityReport(response.data);
      } else {
        setEslintCodeQualityReport({
          status: 'error',
          message: response.data.message || 'Failed to fetch report'
        });
      }
      setEslintReportLoading(false);
    } catch (error) {
      setEslintCodeQualityReport({
        status: 'error',
        message: error.message || 'An error occurred'
      });
      setEslintReportLoading(false);
    }
  };
  
  // Calculate report stats
  const calculateReportStats = (report) => {
    if (!report || !report.report || !Array.isArray(report.report)) {
      return { total: 0, critical: 0, major: 0, minor: 0, info: 0 };
    }
    
    const stats = { total: report.report.length, critical: 0, major: 0, minor: 0, info: 0 };
    
    report.report.forEach(issue => {
      if (issue.severity) {
        switch (issue.severity.toLowerCase()) {
          case 'critical':
            stats.critical++;
            break;
          case 'major':
            stats.major++;
            break;
          case 'minor':
            stats.minor++;
            break;
          case 'info':
            stats.info++;
            break;
          default:
            // No category
            break;
        }
      }
    });
    
    return stats;
  };
  
  // Calculate stats for both reports
  const backendStats = calculateReportStats(codeQualityReport);
  const frontendStats = calculateReportStats(eslintCodeQualityReport);
  
  // Calculate total issues across both reports
  const totalIssues = backendStats.total + frontendStats.total;
  const totalCritical = backendStats.critical + frontendStats.critical;
  const totalMajor = backendStats.major + frontendStats.major;
  const totalMinor = backendStats.minor + frontendStats.minor;
  
  return (
    <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <div className="p-3 rounded-full bg-purple-50 text-purple-600 mr-4">
            <FileCode size={24} />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Code Quality Metrics</h2>
            <p className="text-sm text-gray-500">Backend and frontend code analysis</p>
          </div>
        </div>
        <div>
          <button 
            onClick={() => {
              fetchCodeQualityReport();
              fetchEslintCodeQualityReport();
            }}
            disabled={reportLoading || eslintReportLoading}
            className="p-2 rounded-md hover:bg-gray-100 text-gray-500 transition-colors"
            title="Refresh reports"
          >
            <RefreshCw size={18} className={(reportLoading || eslintReportLoading) ? "animate-spin" : ""} />
          </button>
        </div>
      </div>
      
      {(reportLoading || eslintReportLoading) ? (
        <div className="flex justify-center py-8">
          <div className="animate-pulse flex flex-col items-center">
            <div className="rounded-full bg-gray-200 w-12 h-12 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-24 mb-2.5"></div>
            <div className="h-3 bg-gray-200 rounded w-32"></div>
          </div>
        </div>
      ) : (codeQualityReport?.status === 'error' && eslintCodeQualityReport?.status === 'error') ? (
        <div className="bg-red-50 border border-red-100 rounded-lg p-4 text-red-700">
          <div className="flex mb-2">
            <AlertTriangle size={18} className="mr-2 flex-shrink-0" />
            <h3 className="font-medium">Error retrieving reports</h3>
          </div>
          <p className="text-sm">Unable to fetch code quality metrics</p>
        </div>
      ) : (
        <>
          {/* Combined metrics */}
          <div>
            <div className="grid grid-cols-4 gap-3">
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-gray-800">{totalIssues}</div>
                <div className="text-sm text-gray-500">Total Issues</div>
              </div>
              <div className="bg-red-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-red-600">{totalCritical}</div>
                <div className="text-sm text-red-600">Critical</div>
              </div>
              <div className="bg-orange-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-orange-600">{totalMajor}</div>
                <div className="text-sm text-orange-600">Major</div>
              </div>
              <div className="bg-yellow-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-yellow-600">{totalMinor}</div>
                <div className="text-sm text-yellow-600">Minor</div>
              </div>
            </div>
            {(codeQualityReport?.timestamp || eslintCodeQualityReport?.timestamp) && (
              <div className="flex items-center text-xs text-gray-500 mt-3">
                <Clock size={12} className="mr-1" />
                Last updated: {new Date(Math.max(
                  codeQualityReport?.timestamp ? new Date(codeQualityReport.timestamp).getTime() : 0,
                  eslintCodeQualityReport?.timestamp ? new Date(eslintCodeQualityReport.timestamp).getTime() : 0
                )).toLocaleString()}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
